interface DraftAnswer {
  areaId: number;
  subAreaId: number;
  subAreaDetailId: number;
  answer: number | null;
  loadDate?: string;
}

interface DraftEntry {
  userName?: string;
  requestPeriodid?: number;
  periodName?: string;
  startTime?: string;
  endTime?: string;
  draftAnswers?: DraftAnswer[];
}

export interface DraftRatingsResult {
  ratings: Record<string, number>;
  meta: {
    periods: number;
    entries: number;
  };
}

/**
 * Maps draftAnswers to rating dictionary: "subAreaId-subAreaDetailId" -> answer.
 * If multiple answers for same key, keeps the one with latest loadDate.
 * Skips null answers unless keepNullClears = true and there was a previous non-null (treat as clear).
 */
export const mapDraftAnswersToRatings = (
  draftRoot: DraftEntry[],
  options?: {
    areaFilter?: number;              // restrict to areaId
    keepNullClears?: boolean;         // include null if clearing a previous value
  }
): DraftRatingsResult => {
  const { areaFilter, keepNullClears } = options || {};
  const ratings: Record<string, number> = {};
  const seenDates: Record<string, number> = {};

  draftRoot.forEach(entry => {
    (entry.draftAnswers ?? []).forEach(answer => {
      if (areaFilter && answer.areaId !== areaFilter) return;

      const key = `${answer.subAreaId}-${answer.subAreaDetailId}`;
      const ts = answer.loadDate ? Date.parse(answer.loadDate) : Date.now();

      // Only keep newest
      if (seenDates[key] !== undefined && ts < seenDates[key]) return;

      if (typeof answer.answer === 'number') {
        ratings[key] = answer.answer;
        seenDates[key] = ts;
      } else if (answer.answer === null && keepNullClears && ratings[key] !== undefined) {
        // explicit clear
        delete ratings[key];
        seenDates[key] = ts;
      }
    });
  });

  return {
    ratings,
    meta: {
      periods: draftRoot.length,
      entries: Object.keys(ratings).length
    }
  };
};